import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { strapiDelete, strapiGet, strapiPatch, strapiPost } from '@/lib/strapi'

const BASE = '/api/she-answers/admin'

// ── Types ──────────────────────────────────────────────────────

export interface AdminAgent {
  agent_id: string
  name: string
}

export interface AdminAgentTool {
  type: string
  name: string
  description: string
  api_schema: {
    url: string
    method: 'GET' | 'POST'
    query_params_schema?: { properties: Record<string, any>; required: string[] }
    request_body_schema?: { type: string; properties: Record<string, any>; required: string[] }
  }
}

export interface AdminAgentKbDoc {
  id: string
  name: string
  type: string
}

export interface AdminAgentDetail {
  agent_id: string
  name: string
  conversation_config: {
    agent: {
      first_message: string
      prompt: {
        prompt: string
        tools: AdminAgentTool[]
        knowledge_base: AdminAgentKbDoc[]
        ignore_default_personality: boolean
        rag: { enabled: boolean }
      }
    }
    tts?: { voice_id: string }
  }
}

export interface AdminPhoneNumber {
  phone_number_id: string
  phone_number: string
  label?: string
  assigned_agent?: { agent_id: string; agent_name: string } | null
  imported?: boolean
  twilio_phone_number_sid?: string
}

export interface AdminRestaurantAgent {
  agentId: string
  name: string
  slug: string
  phone: string | null
}

export interface AdminGlobalConfig {
  promptTemplate: string
  tools: AdminAgentTool[]
}

export interface AdminAgentDebugLog {
  id: string
  timestamp: string
  agentId: string
  restaurantSlug: string
  restaurantName: string
  source: 'she_answers' | 'website' | 'unknown'
  toolName: string
  method: string
  path: string
  status: number
  durationMs: number
  request: {
    query: Record<string, unknown>
    body: unknown
  }
  response: unknown
  error?: string
}

export interface SunmiDiagnosticLog {
  id: string
  timestamp: string
  eventType: string
  ok: boolean | null
  restaurantDocumentId: string | null
  restaurantName: string | null
  restaurantSlug: string | null
  deviceId: string | null
  deviceName: string | null
  platform: string | null
  appVersion: string | null
  pushTokenDocumentId: string | null
  orderDocumentId: string | null
  ipAddress: string | null
  userAgent: string | null
  message: string | null
  details: unknown
}

export type OrderMonitorStageStatus = 'ok' | 'fail' | 'waiting' | 'skipped' | 'unknown'

export interface OrderMonitorStage {
  status: OrderMonitorStageStatus
  label: string
  at: string | null
  eventType: string | null
  message: string | null
  deviceName: string | null
  deviceId: string | null
}

export interface OrderMonitorRow {
  orderDocumentId: string
  bookosId: string | null
  restaurant: { documentId: string | null; name: string | null; slug: string | null } | null
  guest: { name: string | null; phone: string | null; email: string | null }
  source: string | null
  fulfillmentMethod: string | null
  asap: boolean
  status: string | null
  date: string | null
  time: string | null
  seenByRestaurant: boolean
  itemCount: number
  items: { name: string; quantity: number }[]
  createdAt: string
  updatedAt: string
  severity: 'healthy' | 'warning' | 'critical'
  reason: string
  ageMinutes: number
  stages: {
    orderCreated: OrderMonitorStage
    backendDispatch: OrderMonitorStage
    deviceReceived: OrderMonitorStage
    restaurantSeen: OrderMonitorStage
    print: OrderMonitorStage
  }
  events: SunmiDiagnosticLog[]
}

export interface OrderMonitorRestaurantOrder {
  orderDocumentId: string
  createdAt: string
  status: string | null
  source: string | null
  fulfillmentMethod: string | null
  asap: boolean
  seenByRestaurant: boolean
  guestName: string | null
  itemCount: number
  severity: 'healthy' | 'warning' | 'critical'
  reason: string
}

export interface OrderMonitorRestaurant {
  restaurant: {
    documentId: string
    name: string | null
    slug: string | null
    phone: string | null
  }
  activeOrders: number
  requestOrders: number
  approvedOrders: number
  readyOrders: number
  deliveryOrders: number
  unseenOrders: number
  healthyOrders: number
  warningOrders: number
  criticalOrders: number
  successPercent: number | null
  latestOrderAt: string | null
  latestFailureAt: string | null
  latestFailureReason: string | null
  currentOrders: OrderMonitorRestaurantOrder[]
}

export interface OrderMonitorResponse {
  generatedAt: string
  counts: {
    total: number
    healthy: number
    warning: number
    critical: number
  }
  restaurants: OrderMonitorRestaurant[]
  rows: OrderMonitorRow[]
}

export interface TwilioSmsDiagnosticLog {
  id: string
  timestamp: string
  eventType: string
  ok: boolean | null
  restaurantDocumentId: string | null
  restaurantName: string | null
  restaurantSlug: string | null
  orderDocumentId: string | null
  source: string | null
  originChannel: string | null
  fulfillmentMethod: string | null
  recipient: string | null
  from: string | null
  messageSid: string | null
  message: string | null
  body: string | null
  details: unknown
}

export interface TwilioMessagingNumber {
  twilio_phone_number_sid: string
  phone_number: string
  label?: string
  capabilities?: {
    voice?: boolean
    sms?: boolean
    mms?: boolean
    fax?: boolean
  }
}

export interface ReadyTimeMessagingRestaurant {
  documentId: string
  name: string
  slug: string
  readyTimeSmsEnabled: boolean
  readyTimeSmsFromNumber: string
  readyTimeWhatsappFromNumbers: string[]
  readyTimeMessageTemplate: string
}

export interface ReadyTimeMessagingSettings {
  defaultTemplate: string
  twilioNumbers: TwilioMessagingNumber[]
  restaurants: ReadyTimeMessagingRestaurant[]
}

// ── Queries ────────────────────────────────────────────────────

export function usePlatformAdminAgents() {
  return useQuery({
    queryKey: ['admin-agents'],
    queryFn: async () => {
      const res = await strapiGet<AdminAgent[]>(`${BASE}/agents`)
      return res.data
    },
    staleTime: 30_000,
  })
}

export function usePlatformAdminAgent(agentId: string | null) {
  return useQuery({
    queryKey: ['admin-agent', agentId],
    queryFn: async () => {
      const res = await strapiGet<AdminAgentDetail>(`${BASE}/agents/${agentId}`)
      return res.data
    },
    enabled: !!agentId,
    staleTime: 10_000,
  })
}

export function usePlatformAdminPhoneNumbers() {
  return useQuery({
    queryKey: ['admin-phone-numbers'],
    queryFn: async () => {
      const res = await strapiGet<AdminPhoneNumber[]>(`${BASE}/phone-numbers`)
      return res.data
    },
    staleTime: 30_000,
  })
}

// ── Mutations ──────────────────────────────────────────────────

export function useAdminUpdatePrompt(agentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (prompt: string) => {
      await strapiPatch(`${BASE}/agents/${agentId}/prompt`, { prompt })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-agent', agentId] }),
  })
}

export function useAdminUpdateTools(agentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (tools: AdminAgentTool[]) => {
      await strapiPatch(`${BASE}/agents/${agentId}/tools`, { tools })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-agent', agentId] }),
  })
}

export function useAdminUpdateKnowledgeBase(agentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (knowledgeBase: AdminAgentKbDoc[]) => {
      await strapiPatch(`${BASE}/agents/${agentId}/knowledge-base`, { knowledgeBase })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-agent', agentId] }),
  })
}

export function useAdminAssignPhone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ phoneNumberId, agentId }: { phoneNumberId: string; agentId: string | null }) => {
      await strapiPatch(`${BASE}/phone-numbers/${phoneNumberId}`, { agentId })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-phone-numbers'] })
      qc.invalidateQueries({ queryKey: ['admin-agents'] })
    },
  })
}

export function useAdminRestaurantAgents() {
  return useQuery({
    queryKey: ['admin-restaurant-agents'],
    queryFn: async () => {
      const res = await strapiGet<AdminRestaurantAgent[]>(`${BASE}/restaurant-agents`)
      return res.data
    },
    staleTime: 30_000,
  })
}

export function useAdminGlobalConfig() {
  return useQuery({
    queryKey: ['admin-global-config'],
    queryFn: async () => {
      const res = await strapiGet<AdminGlobalConfig>(`${BASE}/global-config`)
      return res.data
    },
    staleTime: 30_000,
  })
}

export function useAdminUpdateGlobalConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (config: Partial<AdminGlobalConfig>) => {
      await strapiPatch(`${BASE}/global-config`, config)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-global-config'] }),
  })
}

export function useAdminAgentSignedUrl(agentId: string | null, enabled = false) {
  return useQuery({
    queryKey: ['admin-agent-signed-url', agentId],
    queryFn: async () => {
      const res = await strapiGet<{ url: string }>(`${BASE}/agents/${agentId}/signed-url`)
      return res.data.url
    },
    enabled: !!agentId && enabled,
    staleTime: 50_000, // signed URLs typically valid ~60s
  })
}

export function useAdminAgentDebugLogs(agentId: string | null) {
  return useQuery({
    queryKey: ['admin-agent-debug-logs', agentId],
    queryFn: async () => {
      const res = await strapiGet<AdminAgentDebugLog[]>(`${BASE}/agents/${agentId}/debug-logs`)
      return res.data
    },
    enabled: !!agentId,
    refetchInterval: 3_000,
  })
}

export function useAdminClearAgentDebugLogs(agentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await strapiDelete(`${BASE}/agents/${agentId}/debug-logs`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-agent-debug-logs', agentId] }),
  })
}

export function useAdminSunmiDiagnostics() {
  return useQuery({
    queryKey: ['admin-sunmi-diagnostics'],
    queryFn: async () => {
      const res = await strapiGet<SunmiDiagnosticLog[]>(`${BASE}/sunmi-diagnostics`, { limit: 300 })
      return res.data
    },
    refetchInterval: 3_000,
  })
}

export function useAdminOrderMonitor(params: { restaurantDocumentId?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: ['admin-order-monitor', params.restaurantDocumentId ?? '', params.limit ?? 80],
    queryFn: async () => {
      const res = await strapiGet<OrderMonitorResponse>(`${BASE}/order-monitor`, {
        ...(params.restaurantDocumentId ? { restaurantDocumentId: params.restaurantDocumentId } : {}),
        limit: params.limit ?? 80,
      })
      return res.data
    },
    refetchInterval: 3_000,
  })
}

export function useAdminClearSunmiDiagnostics() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await strapiDelete(`${BASE}/sunmi-diagnostics`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-sunmi-diagnostics'] }),
  })
}

export function useAdminTwilioSmsDiagnostics() {
  return useQuery({
    queryKey: ['admin-twilio-sms-diagnostics'],
    queryFn: async () => {
      const res = await strapiGet<TwilioSmsDiagnosticLog[]>(`${BASE}/twilio-sms-diagnostics`, { limit: 300 })
      return res.data
    },
    refetchInterval: 3_000,
  })
}

export function useAdminClearTwilioSmsDiagnostics() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await strapiDelete(`${BASE}/twilio-sms-diagnostics`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-twilio-sms-diagnostics'] }),
  })
}

export function useAdminReadyTimeMessaging() {
  return useQuery({
    queryKey: ['admin-ready-time-messaging'],
    queryFn: async () => {
      const res = await strapiGet<ReadyTimeMessagingSettings>(`${BASE}/ready-time-messaging`)
      return res.data
    },
    staleTime: 30_000,
  })
}

export function useAdminUpdateReadyTimeMessaging() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      restaurantDocumentId,
      ...data
    }: {
      restaurantDocumentId: string
      readyTimeSmsEnabled?: boolean
      readyTimeSmsFromNumber?: string
      readyTimeWhatsappFromNumbers?: string[]
      readyTimeMessageTemplate?: string
    }) => {
      const res = await strapiPatch<ReadyTimeMessagingRestaurant>(
        `${BASE}/restaurants/${restaurantDocumentId}/ready-time-messaging`,
        data,
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-ready-time-messaging'] })
      qc.invalidateQueries({ queryKey: ['admin-twilio-sms-diagnostics'] })
    },
  })
}

export function useAdminSendReadyTimeSmsTest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      restaurantDocumentId,
      to,
      estimatedReadyMinutes,
      fulfillmentMethod,
    }: {
      restaurantDocumentId: string
      to: string
      estimatedReadyMinutes: number
      fulfillmentMethod: 'pickup' | 'delivery'
    }) => {
      const res = await strapiPost<{ sid: string | null; status: string | null; to: string; from: string | null }>(
        `${BASE}/restaurants/${restaurantDocumentId}/ready-time-sms-test`,
        { to, estimatedReadyMinutes, fulfillmentMethod },
      )
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-twilio-sms-diagnostics'] }),
  })
}
